import {
  clearQuotes,
  postQuotesQueryRequest,
  selectClient,
  selectMaybeClientPartner,
} from '@/model';
import { useFeatureFlags } from '@/contexts/FeatureFlagsContext';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { SHORT_TERM_CARE_PAYMENT_OPTION, type PaymentOption } from '@/features/shop-insurance';
import {
  buildQuoteQueryPayload,
  quoteQueryKey,
} from '@/features/shop-insurance/lib/build-quote-query-payload';

interface UseQuoteFetchingProps {
  open: boolean;
  selectedTab: ShopInsuranceTab;
  paymentOption: PaymentOption;
  hasPartnerWithDemographics: boolean;
  skipAutoFetch?: boolean;
}

export function useQuoteFetching({
  open,
  selectedTab,
  paymentOption,
  hasPartnerWithDemographics,
  skipAutoFetch = false,
}: UseQuoteFetchingProps) {
  const dispatch = useDispatch();
  const client = useSelector(selectClient);
  const clientPartner = useSelector(selectMaybeClientPartner);
  const { flags } = useFeatureFlags();

  const buildPayload = useCallback(
    (policyType: ShopInsuranceTab, cobPaymentOption: PaymentOption) =>
      buildQuoteQueryPayload({
        client,
        clientPartner,
        policyType,
        hasPartnerWithDemographics,
        nycLifeDemo: flags.nycLifeDemo,
        cobPaymentOption,
      }),
    [client, clientPartner, flags.nycLifeDemo, hasPartnerWithDemographics],
  );

  const dispatchQuoteQuery = useCallback(
    (payload: FetchQuotesPayload) => {
      //clear existing quotes before fetching new ones to prevent flash of incorrect quotes when switching between tabs/payment options
      dispatch(clearQuotes());

      dispatch(postQuotesQueryRequest(payload));
    },
    [dispatch],
  );

  const fetchQuotesForPolicyType = (
    policyType: ShopInsuranceTab,
    cobPaymentOption: PaymentOption = paymentOption,
  ) => {
    const payload = buildPayload(policyType, cobPaymentOption);
    if (!payload) {
      return;
    }

    dispatchQuoteQuery(payload);
  };

  const autoFetchPayload = useMemo(
    () => (open && !skipAutoFetch ? buildPayload(selectedTab, paymentOption) : null),
    [open, skipAutoFetch, buildPayload, selectedTab, paymentOption],
  );

  // Keyed on the serialized request rather than on the client object. Mutations such as
  // adding or removing a funding source replace state.client.data with a new reference
  // while leaving every quote input untouched, and refetching there clears the list and
  // resets the shopper's scroll position for no reason.
  const autoFetchKey = quoteQueryKey(autoFetchPayload);

  useEffect(() => {
    if (!open || skipAutoFetch) {
      return;
    }

    // The short-term care view has its own React Query fetch and no life or
    // traditional-LTC quotes; firing this would send carriers a cobPaymentOption
    // they do not recognise. Stale quotes are cleared first — leaving them in
    // redux keeps life-quote chrome (carrier lists, adjusted-quote banners)
    // alive over a list of STC cards.
    if (paymentOption === SHORT_TERM_CARE_PAYMENT_OPTION) {
      dispatch(clearQuotes());
      return;
    }

    if (!autoFetchPayload) {
      return;
    }

    dispatchQuoteQuery(autoFetchPayload);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, skipAutoFetch, paymentOption, dispatch, autoFetchKey, dispatchQuoteQuery]);

  return {
    fetchQuotesForPolicyType,
  };
}
